import CloseIcon from "@mui/icons-material/Close";
import { Tooltip } from "@mui/material";
import LogoIcon from "img/logo_with_name.svg";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IconButton } from "src/components/Default/LekUI/Button";
import Progress from "src/components/ExamComponents/Progress";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import isMobile from "src/helpers/isMobile";
import { getExam, usePatchExam } from "src/hooks/useExam";
import BackIcon from "src/images/Icons/newIcons/back.svg";
import DotListIcon from "src/images/Icons/newIcons/dotList.svg";
import { ExamState, ExamStatus, ExamTypes } from "src/types/enums";

import useWebAppState from "../../../capacitor/useWebAppState";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { ExamTimer } from "./ExamComponents/ExamTimer";

function ExamInfo(props) {
	const { openModal } = useModals();
	const { t } = useTranslation();
	const { id } = useParams();
	const exam = getExam(id);
	const { name, stats, type, startedAt, finishedAt, status, state, pausedAt } =
		exam;
	const { mutateAsync: patchExam, isPending: isLoading } = usePatchExam();
	const isAllResolved = stats.totalQuestions === stats.resolvedQuestions;
	const isSimulationExam = [ExamTypes.SIMULATION, ExamTypes.LEARNING].includes(
		type,
	);

	const [lsValue, setLsValue, { removeItem }] = useLocalStorage(
		`${exam.id}-state`,
		exam.state,
	);

	const togglePause = async () => {
		await patchExam({
			id,
			state: state === ExamState.PAUSED ? ExamState.PENDING : ExamState.PAUSED,
		});
	};

	const setExamState = async (state) => {
		await patchExam({
			id,
			state,
		});
	};

	useWebAppState(
		async () => {
			await setExamState(
				lsValue === ExamState.PAUSED ? ExamState.PAUSED : ExamState.PENDING,
			);
			removeItem();
		},
		async () => {
			setLsValue(exam.state);
			await setExamState(ExamState.PAUSED);
		},
	);

	const onFinishExam = useCallback(async () => {
		await patchExam({ id: exam.id, status: ExamStatus.FINISHED });
		await openModal(ModalsNames.ExamSummaryModal, {
			examId: exam.id,
		});
	}, [exam.id, openModal, patchExam]);

	useEffect(() => {
		if (isAllResolved && status === ExamStatus.ACTIVE && !isSimulationExam) {
			onFinishExam();
		}
	}, [isAllResolved, isSimulationExam, status, onFinishExam]);

	const renderExamTimer = () => {
		if (type === ExamTypes.SIMULATION && status === ExamStatus.ACTIVE) {
			return (
				<ExamTimer
					startedAt={startedAt}
					finishedAt={finishedAt}
					pausedAt={pausedAt}
					state={state}
					onFinished={onFinishExam}
					togglePause={togglePause}
					isLoading={isLoading}
				/>
			);
		}

		return null;
	};

	return (
		<div className="flex flex-col w-100 gap-20">
			<div className="exam-header">
				<div className="flex-center-row gap-8">
					{!isMobile() && renderExamTimer()}
					<IconButton
						data-test-id="close-exam-button"
						onClick={() =>
							openModal(ModalsNames.FinishExamModal, {
								examId: exam.id,
							})
						}
					>
						{isMobile() ? <BackIcon className="icon-normal" /> : <CloseIcon />}
					</IconButton>
				</div>
				<LogoIcon className="logo-32" />
				<IconButton
					onClick={props.toggleIsMenuOpen}
					className="exam-header-drawer--toggle"
				>
					{" "}
					<DotListIcon className="icon-normal" />
				</IconButton>
			</div>
			{isMobile() && renderExamTimer()}
			<div className="exam-header-details">
				<div className="text-field--readonly flex-1 w-50">
					<Tooltip title={name}>
						<span
							className="size-14-light text-ellipsis-1"
							data-test-id="exam-name"
						>
							{name}
						</span>
					</Tooltip>
				</div>
				<Progress
					className="flex-1"
					disablePercent
					allItems={stats.totalQuestions}
					resolvedItems={stats.resolvedQuestions}
					unit={t("units.questions")}
				/>
			</div>
		</div>
	);
}

export default ExamInfo;
